import { onError, ErrorResponse } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { GraphQLFormattedError } from 'graphql';
import * as _ from 'lodash';

import { logout } from '~/contexts/UserAuthProvider';

// Types
type ErrorHandlerResponse = 'forward' | 'terminate';

// Helpers
const getExternalGqlMessage = (
  graphQLError: GraphQLFormattedError,
): string | null => {
  return graphQLError?.extensions?.exception?.externalMessage ?? null;
};

const baseGraphQlErrorHandler = (errorInputs: {
  graphQLErrors: ErrorResponse['graphQLErrors'];
  operation: ErrorResponse['operation'];
}): ErrorHandlerResponse => {
  const { graphQLErrors, operation } = errorInputs;
  if (!graphQLErrors || !graphQLErrors[0]) {
    return 'forward';
  }

  const errMessage =
    getExternalGqlMessage(graphQLErrors[0]) || graphQLErrors[0].message;

  const headers = operation.getContext().headers;
  // handle expired login separately from errors
  const expiredLogin = graphQLErrors.find((err) => {
    return (
      err.extensions?.code === 'UNAUTHENTICATED' &&
      err.message.includes('Login expired')
    );
  });

  if (expiredLogin) {
    // Ask server to clear auth cookie, clear Sentry user scope
    logout();
    operation.setContext({ headers: _.omit(headers, ['authorization']) });
    // returning undefined means no retry on error
    // https://www.apollographql.com/docs/react/data/error-handling/#on-graphql-errors
    return 'terminate';
  }

  // if invalid auth (e.g. invalid JWT), log user out
  const invalidAuth = graphQLErrors.find((err) => {
    return err.extensions?.code === 'UNAUTHENTICATED';
  });
  if (invalidAuth) {
    // Ask server to clear auth cookie, clear Sentry user scope
    logout();
    operation.setContext({ headers: _.omit(headers, ['authorization']) });
    return 'terminate';
  }

  graphQLErrors.forEach((err) => {
    // eslint-disable-next-line no-console
    console.error('[GraphQL Error]', {
      message: err.message,
      path: err.path,
    });

    Sentry.addBreadcrumb({
      category: 'apolloClient.graphQLError',
      message: `[Unexpected GraphQL error]: ${err.message}`,
      level: Sentry.Severity.Error,
      data: {
        errLocations: err.locations,
        errPath: err.path,
        errMessage: err.message,
        error: err,
      },
    });
  });

  Sentry.captureException(new Error(errMessage));

  // We have an unresolved toast error occuring that does
  // not seem to be related to finley systems, suspect heap issue
  // for now, we will (attempt to) silence this error such that
  // customer does not see it.  We will put this early exit after the
  // sentry breadcrumb such that we can continue to monitor / troubleshoot
  if (errMessage.match(/^Unexpected token.*JSON$/)) {
    return 'terminate';
  }

  return 'terminate';
};

const baseNetworkErrorHandler = (
  networkError: ErrorResponse['networkError'],
): ErrorHandlerResponse => {
  if (networkError) {
    Sentry.addBreadcrumb({
      category: 'apolloClient.networkError',
      message: `[Unexpected Network Error]: ${networkError.message}`,
      level: Sentry.Severity.Error,
      data: {
        errPath: networkError.name,
        errStack: networkError.stack,
        errMessage: networkError.message,
        error: networkError,
      },
    });
  }

  return 'terminate';
};

// Exports
export const errorHandlerMiddleware = () => {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    const result = baseGraphQlErrorHandler({ graphQLErrors, operation });
    if (result === 'terminate') return undefined;

    baseNetworkErrorHandler(networkError);
    return forward(operation);
  });
};
